import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { GetActiveReportingYear } from '../services/peerToPeer/PeerToPeerService'
import { ListInvitations, SendInvitations, ResendInvitation, RevokeInvitation } from '../services/peerToPeer/InvitationService'
import { type InvitationDto } from '../models/peerToPeer/Invitations'
import { type P2PReportingYear } from '../models/peerToPeer/Collection'
import { type Response } from './use-fetch'
import { handleApiResponse } from '../utils/apiHelpers'

interface UseP2PInvitationsType {
  year: P2PReportingYear | null
  invitees: InvitationDto[]
  isLoading: boolean
  isSending: boolean
  /** Resolves true when the invitations were accepted, so the caller can clear its draft. */
  send: (emails: string[]) => Promise<boolean>
  resend: (invitationId: number) => Promise<void>
  revoke: (invitationId: number) => Promise<void>
}

export const useP2PInvitations = (isdOrganizationId: number): UseP2PInvitationsType => {
  const [year, setYear] = useState<P2PReportingYear | null>(null)
  const [invitees, setInvitees] = useState<InvitationDto[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSending, setIsSending] = useState<boolean>(false)

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    const activeYear = await handleApiResponse(GetActiveReportingYear())
    if (activeYear != null) {
      setYear(activeYear)
      setInvitees(await handleApiResponse(ListInvitations(isdOrganizationId, activeYear.id)) ?? [])
    }
    setIsLoading(false)
  }, [isdOrganizationId])

  useEffect(() => { void load() }, [load])

  const send = async (emails: string[]): Promise<boolean> => {
    if (year == null || emails.length === 0) return false
    setIsSending(true)
    const result = await handleApiResponse(SendInvitations({ isdOrganizationId, reportingYearId: year.id, emails }))
    setIsSending(false)
    if (result == null) return false

    toast.success(`${result.invited.length} invitation(s) sent`)
    if (result.skipped.length > 0) toast.warn(`${result.skipped.length} address(es) skipped`)
    if (result.alreadyInvited.length > 0) toast.info(`${result.alreadyInvited.length} address(es) already invited: ${result.alreadyInvited.join(', ')}`)
    await load()
    return true
  }

  const actOnInvitation = async (call: Promise<Response<unknown>>, message: string): Promise<void> => {
    const result = await handleApiResponse(call)
    if (result === null) return
    toast.success(message)
    await load()
  }

  return {
    year,
    invitees,
    isLoading,
    isSending,
    send,
    resend: async invitationId => { await actOnInvitation(ResendInvitation(invitationId), 'Invitation resent') },
    revoke: async invitationId => { await actOnInvitation(RevokeInvitation(invitationId), 'Invitation revoked') }
  }
}
